import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { blrKg } from '../../helpers/blr';
import { preStrandingCopperKgPerM } from '../../helpers/drumCapacity';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useDrums, useCreateDrum } from '../../features/drums/hooks/useDrums.js';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import DrumViewer from '../../components/standard/DrumViewer.js';
import { API } from '../../config/api';
import { formatCoreSpec } from '../../helpers/productLabel';

const CABLE_COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  BLACK: '#333333', WHITE: '#EEEEEE', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};

// colour of the wound cable in the 3D preview, by production phase
const cableHexFor = (phaseName, productColor) => {
  const p = String(phaseName || '').toLowerCase();
  if (['unwinding', 'pre_stranding', 'rope', 'conductor_isolation'].includes(p)) {
    return '#B87333'; // bare copper
  }
  if (p === 'interior_sheat') return '#EAEAEA';
  // exterior_sheat / armouring / coiling → finished product colour
  return CABLE_COLOR_HEX[String(productColor || '').toUpperCase()] || '#B87333';
};

const CUSTOM_DRUM = '__custom__';

const CABLE_NAME_OPTIONS = CABLE_NAMES_NO_ROPE;

const COLOR_OPTIONS = CABLE_COLORS;

const PHASE_LABELS = {
  pre_stranding: 'Pre Stranding',
  conductor_isolation: 'Conductor Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  exterior_sheat: 'Exterior Sheath',
  armouring: 'Armouring',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const createEmptyProduct = () => ({
  name: '',
  numberOfConductors: '',
  section: '',
  color: '',
  hasMixedConductorSections: false,
  greenYellowSection: ''
});

const toNum = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round = (value, digits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(digits));
};

const getPhaseLabel = phaseName => PHASE_LABELS[phaseName] || phaseName;

const getUsablePhaseDiameter = phase => {
  if (!phase || typeof phase !== 'object') return null;

  if (Number.isFinite(Number(phase.diameter)) && Number(phase.diameter) > 0) {
    return Number(phase.diameter);
  }

  if (
    phase.name === 'conductor_isolation' &&
    Array.isArray(phase.conductors) &&
    phase.conductors.length > 0
  ) {
    const diameters = phase.conductors
      .map(c => {
        const isoDiameter = Number(c?.isolation?.diameter);
        const wireDiameter = Number(c?.wire?.diameter);

        if (Number.isFinite(isoDiameter) && isoDiameter > 0) return isoDiameter;
        if (Number.isFinite(wireDiameter) && wireDiameter > 0)
          return wireDiameter;

        return null;
      })
      .filter(Boolean);

    if (diameters.length > 0) {
      return Math.max(...diameters);
    }
  }

  if (Number.isFinite(Number(phase?.wire?.diameter))) {
    return Number(phase.wire.diameter);
  }

  return null;
};

const extractPhaseOptions = standard => {
  if (!standard || !Array.isArray(standard.phases)) return [];

  const options = [];

  standard.phases.forEach((phase, index) => {
    // conductor_isolation with multiple conductors: one option per unique diameter
    if (
      phase.name === 'conductor_isolation' &&
      Array.isArray(phase.conductors) &&
      phase.conductors.length > 0
    ) {
      const seen = new Map(); // diameter -> conductor index
      phase.conductors.forEach((cond, ci) => {
        const isoDiam = Number(cond?.isolation?.diameter);
        const wireDiam = Number(cond?.wire?.diameter);
        const d = (Number.isFinite(isoDiam) && isoDiam > 0) ? isoDiam : wireDiam;
        if (!Number.isFinite(d) || d <= 0) return;

        const dKey = d.toFixed(3);
        if (seen.has(dKey)) return; // skip duplicate diameters
        seen.set(dKey, ci);

        const section = cond?.wire?.section || cond?.wire?.diameter || '';
        const color = cond?.isolation?.color || '';
        const label = `${getPhaseLabel(phase.name)} — Ø${d}mm${color ? ` (${color})` : ''}`;

        options.push({
          key: `${phase.name}-${index}-cond${ci}`,
          name: phase.name,
          label,
          diameter: d,
          phase,
          conductorIndex: ci
        });
      });
      return;
    }

    // pre_stranding with rope array: one option per unique rope diameter
    if (
      phase.name === 'pre_stranding' &&
      Array.isArray(phase.rope) &&
      phase.rope.length > 0
    ) {
      const seen = new Map();
      phase.rope.forEach((rope, ri) => {
        const d = Number(rope?.diameter);
        if (!Number.isFinite(d) || d <= 0) return;
        const dKey = d.toFixed(3);
        if (seen.has(dKey)) return;
        seen.set(dKey, ri);

        options.push({
          key: `${phase.name}-${index}-rope${ri}`,
          name: phase.name,
          label: `${getPhaseLabel(phase.name)} — Ø${d}mm`,
          diameter: d,
          phase,
          ropeIndex: ri
        });
      });
      return;
    }

    // default: single option per phase
    const diameter = getUsablePhaseDiameter(phase);
    if (!Number.isFinite(diameter) || diameter <= 0) return;

    options.push({
      key: `${phase.name}-${index}`,
      name: phase.name,
      label: getPhaseLabel(phase.name),
      diameter,
      phase
    });
  });

  return options;
};

const getRecommendedUsableWidthFactor = drum => {
  const d = Number(drum?.drumDiameter || 0);

  if (d <= 1000) return 0.7;
  if (d >= 1250) return 0.8;

  return 0.95;
};

const estimateDrumCapacity = ({
  drumDiameter,
  barrelDiameter,
  flangeWidth,
  cableDiameter,
  fillFactor = 0.9,
  usableWidthFactor = 1
}) => {
  const D = Number(drumDiameter);
  const d = Number(barrelDiameter);
  const B = Number(flangeWidth) * Number(usableWidthFactor);
  const dc = Number(cableDiameter);
  const k = Number(fillFactor);

  if (![D, d, B, dc, k].every(v => Number.isFinite(v) && v > 0)) {
    return null;
  }

  if (D <= d) {
    return {
      error:
        'Diametrul exterior al tamburului trebuie sa fie mai mare decat diametrul tobei.'
    };
  }

  if (dc >= B) {
    return {
      error:
        'Diametrul cablului este mai mare sau egal cu latimea utila a tamburului.'
    };
  }

  const theoreticalLength = ((D * D - d * d) * B) / (1000 * dc * dc);
  const estimatedLength = theoreticalLength * k;
  const radialBuild = (D - d) / 2;
  const approxLayers = radialBuild / dc;
  const approxTurnsPerLayer = B / dc;

  return {
    theoreticalLength: round(theoreticalLength, 2),
    estimatedLength: round(estimatedLength, 2),
    radialBuild: round(radialBuild, 2),
    approxLayers: round(approxLayers, 1),
    approxTurnsPerLayer: round(approxTurnsPerLayer, 1)
  };
};

const StandardDrumLength = () => {
  const [product, setProduct] = useState(createEmptyProduct());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [standard, setStandard] = useState(null);

  const [packingFactor, setPackingFactor] = useState(0.8);
  const [usableWidthFactor, setUsableWidthFactor] = useState(0.95);

  const [selectedPhaseKey, setSelectedPhaseKey] = useState('');
  const [selectedDrumId, setSelectedDrumId] = useState('');
  const [requestedLength, setRequestedLength] = useState('');
  const [manualCableDiameter, setManualCableDiameter] = useState('');
  const [customDrum, setCustomDrum] = useState({
    drumType: 'metal',
    drumDiameter: '',
    flangeWidth: '',
    barrelDiameter: ''
  });
  const [savingDrum, setSavingDrum] = useState(false);

  const { data: drumsData, isLoading: drumsLoading } = useDrums();
  const DRUMS = drumsData?.data || [];
  const createDrum = useCreateDrum();

  // Auto-select first drum when drums load
  useEffect(() => {
    if (DRUMS.length > 0 && !selectedDrumId) {
      setSelectedDrumId(DRUMS[0]._id);
    }
  }, [DRUMS, selectedDrumId]);

  const isSearchValid =
    product.name.trim() !== '' &&
    product.numberOfConductors !== '' &&
    product.section !== '' &&
    product.color.trim() !== '' &&
    (!product.hasMixedConductorSections || product.greenYellowSection !== '');

  const handleChange = (field, value) => {
    setProduct(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'hasMixedConductorSections' && !value
        ? { greenYellowSection: '' }
        : {})
    }));
  };

  const fetchStandard = async () => {
    try {
      setLoading(true);
      setError('');
      setStandard(null);
      setSelectedPhaseKey('');
      setRequestedLength('');
      setManualCableDiameter('');

      const payload = [
        {
          name: product.name,
          numberOfConductors: Number(product.numberOfConductors),
          section: Number(product.section),
          color: product.color,
          hasMixedConductorSections: Boolean(product.hasMixedConductorSections),
          greenYellowSection: product.hasMixedConductorSections
            ? Number(product.greenYellowSection)
            : null
        }
      ];

      const response = await axios.post(
        `${API.BACKEND}/api/v1/standard/find-many`,
        payload
      );

      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      const firstStandard = data?.[0] || null;

      setStandard(firstStandard);

      const phaseOptions = extractPhaseOptions(firstStandard);
      const firstPhaseKey = phaseOptions[0]?.key || '';
      setSelectedPhaseKey(firstPhaseKey);

      const firstPhaseDiameter = phaseOptions[0]?.diameter || '';
      setManualCableDiameter(
        firstPhaseDiameter ? String(firstPhaseDiameter) : ''
      );
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          'Nu am putut incarca standardul. Verifica API-ul.'
      );
    } finally {
      setLoading(false);
    }
  };

  const phaseOptions = useMemo(() => {
    return extractPhaseOptions(standard);
  }, [standard]);

  const selectedPhase = useMemo(() => {
    return (
      phaseOptions.find(phase => phase.key === selectedPhaseKey) ||
      phaseOptions[0] ||
      null
    );
  }, [phaseOptions, selectedPhaseKey]);

  const selectedDrum = useMemo(() => {
    if (selectedDrumId === CUSTOM_DRUM) {
      return {
        _id: CUSTOM_DRUM,
        custom: true,
        drumType: customDrum.drumType,
        drumDiameter: toNum(customDrum.drumDiameter),
        flangeWidth: toNum(customDrum.flangeWidth),
        barrelDiameter: toNum(customDrum.barrelDiameter)
      };
    }
    return DRUMS.find(drum => drum._id === selectedDrumId) || DRUMS[0] || null;
  }, [selectedDrumId, DRUMS, customDrum]);

  useEffect(() => {
    if (!selectedPhase) return;

    const phaseName = selectedPhase?.name || '';
    const isSheatPhase =
      phaseName === 'interior_sheat' || phaseName === 'exterior_sheat';
    const isRope = phaseName === 'rope';

    // auto fill/width factors per phase type
    if (isSheatPhase) {
      setPackingFactor(0.9);
      setUsableWidthFactor(1);
    } else if (isRope) {
      setPackingFactor(0.8);
      setUsableWidthFactor(0.9);
    } else {
      setPackingFactor(0.8);
      setUsableWidthFactor(getRecommendedUsableWidthFactor(selectedDrum));
    }

    const phaseDiameter = Number(selectedPhase?.diameter || 0);
    setManualCableDiameter(phaseDiameter > 0 ? String(phaseDiameter) : '');
  }, [selectedPhaseKey, selectedDrumId]);

  const cableDiameter = useMemo(() => {
    const manual = Number(manualCableDiameter);
    if (Number.isFinite(manual) && manual > 0) return manual;

    return Number(selectedPhase?.diameter || 0);
  }, [manualCableDiameter, selectedPhase]);

  const estimate = useMemo(() => {
    if (!selectedPhase || !selectedDrum) return null;

    return estimateDrumCapacity({
      drumDiameter: selectedDrum.drumDiameter,
      barrelDiameter: selectedDrum.barrelDiameter,
      flangeWidth: selectedDrum.flangeWidth,
      cableDiameter,
      fillFactor: packingFactor,
      usableWidthFactor
    });
  }, [
    selectedPhase,
    selectedDrum,
    cableDiameter,
    packingFactor,
    usableWidthFactor
  ]);

  const requestedLengthNum = toNum(requestedLength);

  // custom third-party drum: validity + save-to-library
  const customDrumValid =
    toNum(customDrum.drumDiameter) > 0 &&
    toNum(customDrum.flangeWidth) > 0 &&
    toNum(customDrum.barrelDiameter) > 0 &&
    toNum(customDrum.drumDiameter) > toNum(customDrum.barrelDiameter);

  const saveCustomDrum = async () => {
    if (!customDrumValid) return;
    setSavingDrum(true);
    try {
      await createDrum.mutateAsync({
        drumType: customDrum.drumType,
        drumDiameter: toNum(customDrum.drumDiameter),
        flangeWidth: toNum(customDrum.flangeWidth),
        barrelDiameter: toNum(customDrum.barrelDiameter),
        notes: 'custom / third-party'
      });
      alert('Drum saved to the library.');
    } catch (e) {
      alert(
        'Could not save drum: ' +
          (e?.response?.data?.error || e?.message || 'unknown error')
      );
    } finally {
      setSavingDrum(false);
    }
  };

  // how full the drum is (for the 3D wound-cable build)
  const fillFraction =
    requestedLengthNum > 0 && toNum(estimate?.estimatedLength) > 0
      ? Math.min(1, requestedLengthNum / toNum(estimate.estimatedLength))
      : 1;
  const cableHex = cableHexFor(selectedPhase?.name, product.color);

  // ── weight calculation (uses requestedLength, accumulates all materials up to selected phase) ──
  const weightEstimate = useMemo(() => {
    const length_m = requestedLengthNum > 0 ? requestedLengthNum : toNum(estimate?.estimatedLength);
    if (!length_m || !selectedPhase?.phase || !standard?.phases) return null;

    const allPhases = standard.phases;
    const targetPhaseName = selectedPhase.phase.name;
    const PI = Math.PI;

    let copperKg = 0;
    let pvcKg = 0;
    let steelKg = 0;

    // helper: copper kg for a single wire per meter
    const wireKgPerM = (d, density = 8.96) => {
      const area_mm2 = (PI * Number(d) * Number(d)) / 4;
      return (area_mm2 * 1e-6) * density * 1000;
    };

    // helper: add all copper from conductor_isolation phase
    const addCopperFromCI = (ciPhase, len) => {
      if (!ciPhase?.conductors) return;
      for (const cond of ciPhase.conductors) {
        const wire = cond.wire;
        if (!wire) continue;
        if (wire.type?.toUpperCase() === 'SINGLE-WIRE') {
          copperKg += wireKgPerM(wire.diameter, Number(wire.density) || 8.96) * len;
        } else {
          const diams = Array.isArray(wire.wireDiameter) ? wire.wireDiameter : [wire.wireDiameter].filter(Boolean);
          const counts = Array.isArray(wire.numberOfWires) ? wire.numberOfWires : [wire.numberOfWires].filter(Boolean);
          diams.forEach((d, i) => {
            copperKg += wireKgPerM(d, Number(wire.density) || 8.96) * len * (Number(counts[i]) || 1);
          });
        }
      }
    };

    // helper: add all isolation PVC from conductor_isolation
    const addIsoFromCI = (ciPhase, len) => {
      if (!ciPhase?.conductors) return;
      for (const cond of ciPhase.conductors) {
        const iso = cond.isolation;
        if (iso?.fillingSection && iso?.density) {
          pvcKg += (Number(iso.fillingSection) * Number(iso.density) * len) / 1000;
        }
      }
    };

    // helper: add sheat/mantle PVC
    const addSheatPVC = (phase, len) => {
      if (phase?.fillingSection && phase?.density) {
        pvcKg += (Number(phase.fillingSection) * Number(phase.density) * len) / 1000;
      }
    };

    // helper: add armouring BLR (steel tape) — shared 2-tape + overlap formula
    const addArmouring = (phase, len) => {
      if (phase?.wide && phase?.thickness) {
        steelKg += blrKg(phase.wide, phase.thickness, Number(phase.density) || 7.85, len);
      }
    };

    const ciPhase = allPhases.find(p => p.name === 'conductor_isolation');
    const intPhase = allPhases.find(p => p.name === 'interior_sheat');
    const armPhase = allPhases.find(p => p.name === 'armouring');
    const extPhase = allPhases.find(p => p.name === 'exterior_sheat');

    // determine which phases are "at or before" the selected phase
    const phaseOrder = ['unwinding', 'pre_stranding', 'conductor_isolation', 'rope', 'interior_sheat', 'armouring', 'exterior_sheat'];
    const targetIdx = phaseOrder.indexOf(targetPhaseName);

    // helper: add copper from a single wire spec (works for both conductor.wire and rope entry)
    const addCopperFromWireSpec = (wire, len) => {
      if (!wire) return;
      const density = Number(wire.density) || 8.96;
      if (wire.type?.toUpperCase() === 'SINGLE-WIRE') {
        copperKg += wireKgPerM(wire.diameter, density) * len;
      } else {
        const diams = Array.isArray(wire.wireDiameter) ? wire.wireDiameter : [wire.wireDiameter].filter(Boolean);
        const counts = Array.isArray(wire.numberOfWires) ? wire.numberOfWires : [wire.numberOfWires].filter(Boolean);
        diams.forEach((d, i) => {
          copperKg += wireKgPerM(d, density) * len * (Number(counts[i]) || 1);
        });
      }
    };

    if (targetPhaseName === 'conductor_isolation') {
      // ONE conductor — use the selected conductor index from phase option
      const condIdx = selectedPhase?.conductorIndex ?? 0;
      const cond = ciPhase?.conductors?.[condIdx];
      if (cond) {
        addCopperFromWireSpec(cond.wire, length_m);
        const iso = cond.isolation;
        if (iso?.fillingSection && iso?.density) {
          pvcKg += (Number(iso.fillingSection) * Number(iso.density) * length_m) / 1000;
        }
      }
    } else if (targetPhaseName === 'pre_stranding') {
      // A pre-stranding run makes ONE stranded conductor, so the drum carries one
      // rope's copper. Falling back to every conductor overstated a 5-core drum
      // fivefold — and it fell back on 65 of the 81 standards that have this
      // phase, because those keep the spec flat on the phase rather than in a
      // rope[] array. preStrandingCopperKgPerM handles both shapes, and applies
      // numberOfDrums for the stranded case.
      const psPhase = allPhases.find(p => p.name === 'pre_stranding');
      const kgPerM = preStrandingCopperKgPerM(psPhase, selectedPhase?.ropeIndex ?? 0);
      if (kgPerM != null) {
        copperKg += kgPerM * length_m;
      } else {
        // no rope spec at all — one conductor is the closest honest stand-in,
        // never the whole cable
        const cond = ciPhase?.conductors?.[0];
        if (cond) addCopperFromWireSpec(cond.wire, length_m);
      }
    } else if (targetPhaseName === 'unwinding') {
      addCopperFromCI(ciPhase, length_m);
    } else if (targetIdx >= phaseOrder.indexOf('rope')) {
      // rope and beyond: all conductors
      addCopperFromCI(ciPhase, length_m);
      addIsoFromCI(ciPhase, length_m);
    }

    // add interior sheat PVC if target is at or after interior_sheat
    if (targetIdx >= phaseOrder.indexOf('interior_sheat')) {
      addSheatPVC(intPhase, length_m);
    }

    // add armouring steel if target is at or after armouring
    if (targetIdx >= phaseOrder.indexOf('armouring')) {
      addArmouring(armPhase, length_m);
    }

    // add exterior sheat PVC if target is at or after exterior_sheat
    if (targetIdx >= phaseOrder.indexOf('exterior_sheat')) {
      addSheatPVC(extPhase, length_m);
    }

    const totalKg = copperKg + pvcKg + steelKg;
    return totalKg > 0 ? { totalKg: round(totalKg, 2), copperKg: round(copperKg, 2), pvcKg: round(pvcKg, 2), steelKg: round(steelKg, 2) } : null;
  }, [requestedLengthNum, estimate, selectedPhase, standard]);

  const estimatedLengthNum = toNum(estimate?.estimatedLength);
  const hasRequestedLength = requestedLength !== '' && requestedLengthNum > 0;
  const fits = hasRequestedLength
    ? estimatedLengthNum >= requestedLengthNum
    : null;

  return (
    <div className="mb-4">
      <h1 className="fw-semibold mb-3">Standard Drum Length</h1>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Packing / Fill Factor</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.5"
                max="1"
                value={packingFactor}
                onChange={e => setPackingFactor(Number(e.target.value))}
              />
              <div className="small text-muted mt-1">
                Recomandat: 0.80 - 0.90
              </div>
            </Col>

            <Col md={4}>
              <Form.Label>Usable Width Factor</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.5"
                max="1"
                value={usableWidthFactor}
                onChange={e => setUsableWidthFactor(Number(e.target.value))}
              />
              <div className="small text-muted mt-1">
                Auto: 1000 → 0.70 | 1250+ → 0.80
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold">Search Standard</div>

          <Form.Check
            type="switch"
            label="GY different section"
            checked={product.hasMixedConductorSections}
            onChange={e =>
              handleChange('hasMixedConductorSections', e.target.checked)
            }
          />
        </Card.Header>

        <Card.Body>
          <Form>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Cable Name *</Form.Label>
                <Form.Select
                  value={product.name}
                  onChange={e => handleChange('name', e.target.value)}
                >
                  <option value="">Select</option>
                  {CABLE_NAME_OPTIONS.map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Conductors *</Form.Label>
                <Form.Control
                  type="number"
                  value={product.numberOfConductors}
                  onChange={e =>
                    handleChange('numberOfConductors', e.target.value)
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Label>Main Section *</Form.Label>
                <Form.Control
                  type="number"
                  value={product.section}
                  onChange={e => handleChange('section', e.target.value)}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Color *</Form.Label>
                <Form.Select
                  value={product.color}
                  onChange={e => handleChange('color', e.target.value)}
                >
                  <option value="">Select</option>
                  {COLOR_OPTIONS.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {product.hasMixedConductorSections && (
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Label className="text-success">
                    Green-Yellow Section *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={product.greenYellowSection}
                    onChange={e =>
                      handleChange('greenYellowSection', e.target.value)
                    }
                  />
                </Col>
              </Row>
            )}

            <Row className="mt-4">
              <Col className="text-end">
                <Button
                  variant="primary"
                  disabled={!isSearchValid || loading}
                  onClick={fetchStandard}
                >
                  {loading ? <Spinner size="sm" /> : 'Search Standard'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {standard && (
        <Card className="mb-4 shadow-sm">
          <Card.Header className="fw-semibold">
            {standard?.name} - {formatCoreSpec(product)}
          </Card.Header>

          <Card.Body>
            {phaseOptions.length === 0 ? (
              <Alert variant="warning" className="mb-0">
                Standardul a fost gasit, dar nu exista faze cu diametru
                utilizabil pentru calcul.
              </Alert>
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Label>Select Phase</Form.Label>
                    <Form.Select
                      value={selectedPhaseKey}
                      onChange={e => setSelectedPhaseKey(e.target.value)}
                    >
                      {phaseOptions.map(phase => (
                        <option key={phase.key} value={phase.key}>
                          {phase.label} - Ø {round(phase.diameter, 3)} mm
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={3}>
                    <Form.Label>Select Drum</Form.Label>
                    <Form.Select
                      value={selectedDrumId}
                      onChange={e => setSelectedDrumId(e.target.value)}
                      disabled={drumsLoading}
                    >
                      {drumsLoading && <option>Loading drums...</option>}
                      {DRUMS.map(drum => (
                        <option key={drum._id} value={drum._id}>
                          {drum.drumType.charAt(0).toUpperCase() + drum.drumType.slice(1)} | D={drum.drumDiameter} | B=
                          {drum.flangeWidth} | d={drum.barrelDiameter}
                        </option>
                      ))}
                      <option value={CUSTOM_DRUM}>➕ Custom drum (third-party)…</option>
                    </Form.Select>
                  </Col>

                  <Col md={3}>
                    <Form.Label>Requested Length (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={requestedLength}
                      onChange={e => setRequestedLength(e.target.value)}
                      placeholder="Ex: 500"
                    />
                  </Col>

                  <Col md={3}>
                    <Form.Label>Cable Diameter (editable)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.001"
                      value={manualCableDiameter}
                      onChange={e => setManualCableDiameter(e.target.value)}
                    />
                  </Col>
                </Row>

                {selectedDrumId === CUSTOM_DRUM && (
                  <Row className="mb-3 g-2 align-items-end mx-0 p-2 bg-body-secondary rounded">
                    <Col xs={12}>
                      <small className="fw-bold text-700">
                        Custom third-party drum — enter measured dimensions (mm)
                      </small>
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label className="mb-0 fs-10">Material</Form.Label>
                      <Form.Select
                        size="sm"
                        value={customDrum.drumType}
                        onChange={e => setCustomDrum({ ...customDrum, drumType: e.target.value })}
                      >
                        <option value="metal">Metal</option>
                        <option value="wood">Wood</option>
                        <option value="plastic">Plastic</option>
                      </Form.Select>
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label className="mb-0 fs-10">D — flange Ø</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={customDrum.drumDiameter}
                        onChange={e => setCustomDrum({ ...customDrum, drumDiameter: e.target.value })}
                        placeholder="1250"
                      />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label className="mb-0 fs-10">B — width</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={customDrum.flangeWidth}
                        onChange={e => setCustomDrum({ ...customDrum, flangeWidth: e.target.value })}
                        placeholder="800"
                      />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label className="mb-0 fs-10">d — barrel Ø</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={customDrum.barrelDiameter}
                        onChange={e => setCustomDrum({ ...customDrum, barrelDiameter: e.target.value })}
                        placeholder="630"
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={savingDrum || !customDrumValid}
                        onClick={saveCustomDrum}
                      >
                        {savingDrum ? 'Saving…' : 'Save to drum library'}
                      </Button>
                      {!customDrumValid && (
                        <div className="text-danger fs-10 mt-1">
                          D must be &gt; d, and all three &gt; 0.
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col>
                    <Alert variant="info" className="mb-0">
                      <b>L = ((D² - d²) × B) / (1000 × dc²)</b> then is applied
                      filling factor.
                    </Alert>
                  </Col>
                  <Col>
                    {hasRequestedLength && (
                      <Alert
                        variant={fits ? 'success' : 'warning'}
                        className="mb-0"
                      >
                        {fits ? (
                          <>
                            Passed <b>{requestedLengthNum} m</b>
                          </>
                        ) : (
                          <>
                            Fail <b>{requestedLengthNum} m</b>
                          </>
                        )}
                      </Alert>
                    )}
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col lg={7}>
                {estimate?.error ? (
                  <Alert variant="danger">{estimate.error}</Alert>
                ) : (
                  <Table bordered responsive className="mb-3">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>Product Standard</th>
                        <td>{standard.productStandard || '-'}</td>
                      </tr>
                      <tr>
                        <th>Selected Phase</th>
                        <td>{selectedPhase?.label || '-'}</td>
                      </tr>
                      <tr>
                        <th>Cable Diameter Used In Calculation</th>
                        <td>
                          <b>{round(cableDiameter, 3)} mm</b>
                        </td>
                      </tr>
                      <tr>
                        <th>Drum</th>
                        <td>
                          {selectedDrum?.drumType} | D=
                          {selectedDrum?.drumDiameter} mm | B=
                          {selectedDrum?.flangeWidth} mm | d=
                          {selectedDrum?.barrelDiameter} mm
                        </td>
                      </tr>
                      <tr>
                        <th>Usable Width Factor</th>
                        <td>{usableWidthFactor}</td>
                      </tr>
                      <tr>
                        <th>Theoretical Length</th>
                        <td>{estimate?.theoreticalLength} m</td>
                      </tr>
                      <tr>
                        <th>Estimated Real Length</th>
                        <td>
                          <b>{estimate?.estimatedLength} m</b>
                        </td>
                      </tr>
                      <tr>
                        <th>Requested Length</th>
                        <td>
                          {hasRequestedLength ? `${requestedLengthNum} m` : '-'}
                        </td>
                      </tr>
                      <tr>
                        <th>Approx Layers</th>
                        <td>{estimate?.approxLayers}</td>
                      </tr>
                      <tr>
                        <th>Approx Turns / Layer</th>
                        <td>{estimate?.approxTurnsPerLayer}</td>
                      </tr>
                      <tr>
                        <th>Radial Build Available</th>
                        <td>{estimate?.radialBuild} mm</td>
                      </tr>
                      {weightEstimate && (
                        <>
                          <tr className="table-info">
                            <th>Total Weight (for {requestedLengthNum > 0 ? `${requestedLengthNum}m` : 'estimated length'})</th>
                            <td><b>{weightEstimate.totalKg} kg</b></td>
                          </tr>
                          {weightEstimate.copperKg > 0 && (
                            <tr>
                              <th>— Copper</th>
                              <td>{weightEstimate.copperKg} kg</td>
                            </tr>
                          )}
                          {weightEstimate.pvcKg > 0 && (
                            <tr>
                              <th>— PVC / Compound</th>
                              <td>{weightEstimate.pvcKg} kg</td>
                            </tr>
                          )}
                          {weightEstimate.steelKg > 0 && (
                            <tr>
                              <th>— BLR (armouring)</th>
                              <td>{weightEstimate.steelKg} kg</td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </Table>
                )}
                  </Col>
                  <Col lg={5}>
                    <DrumViewer
                      drum={selectedDrum}
                      cableDiameterMm={cableDiameter}
                      fillFraction={fillFraction}
                      cableHex={cableHex}
                    />
                    <div className="text-center text-700 fs-10 mt-1">
                      Cable wound to <b>{Math.round(fillFraction * 100)}%</b> of capacity ·{' '}
                      {selectedDrum?.custom ? 'custom drum' : selectedDrum?.drumType}
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default StandardDrumLength;
